import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, ShieldCheck, Users, CheckCircle2, ArrowRight, Clock, Zap, MessageCircle, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { codeFor, SCHENGEN_COUNTRIES } from "@/lib/countries";
import { WORLD_COUNTRIES, SCHENGEN_CODES, VISA_FREE_CODES, worldCountryByCode } from "@/lib/worldCountries";
import { ADD_ONS, ADD_ONS_BY_ID, addOnPrice, labelForItems } from "@/lib/addons";
import { PreviewCard } from "@/components/DocumentMockups";
import ScrollRow from "@/components/ScrollRow";
import PhoneInput from "@/components/PhoneInput";
import EligibilityBanner from "@/components/EligibilityBanner";
import { CountryCombobox } from "@/components/CountryCombobox";
import { SEO } from "@/components/SEO";

const PACKAGE_ICON = { basic: ShieldCheck, complete: Sparkles, premium: Users };
const isSupportItem = (it) => /whatsapp|24\/?7|support/i.test(it?.title || "");

export default function Apply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(location.search);
  const initialPackage = params.get("package") || "complete";
  const initialDelivery = params.get("delivery") === "express" ? "express" : "normal";
  const initialCountry = params.get("country") || "";
  const initialNationality = (params.get("nationality") || "").toUpperCase();
  const initialApplyingFrom = (params.get("applying_from") || "").toUpperCase();
  const validAddonIds = new Set(ADD_ONS.map((a) => a.id));
  const initialAddOns = (params.get("addons") || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => validAddonIds.has(s));

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    whatsapp: "",
    nationality: initialNationality,                // ISO-2 code
    applying_from_country: initialApplyingFrom,     // ISO-2 code
    applying_from_city: "",                         // free-text city
    destination_country: initialCountry,
    num_applicants: 1,
    package_id: initialPackage,
    delivery_type: initialDelivery,
    notes: "",
    add_ons: initialAddOns,
  });

  const isSchengenNational = !!form.nationality && SCHENGEN_CODES.has(form.nationality);
  const [visaFreeAck, setVisaFreeAck] = useState(false);
  const isVisaFreeUnacked = !!form.nationality && VISA_FREE_CODES.has(form.nationality) && !visaFreeAck;
  const blocked = isSchengenNational || isVisaFreeUnacked;
  const applyingFromName = worldCountryByCode(form.applying_from_country)?.name || "";

  // Reset visa-free acknowledgement when nationality changes
  useEffect(() => { setVisaFreeAck(false); }, [form.nationality]);

  useEffect(() => {
    // Always start at the top of the page on load / navigation
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const code = codeFor(form.destination_country);
    const endpoint = code ? `/packages/${code}` : "/packages";
    api.get(endpoint).then((r) => setPackages(r.data)).catch(() => {});
  }, [form.destination_country]);

  useEffect(() => {
    if (user && user.email && !form.email) {
      setForm((f) => ({ ...f, email: user.email, full_name: user.name || "" }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blocked) {
      toast.error(isSchengenNational
        ? "Schengen-Area nationals do not need a Schengen visa."
        : "Please acknowledge the visa-free notice to continue.");
      return;
    }
    if (!user) {
      navigate(`/login?redirect=/apply${location.search}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", form);
      navigate(`/apply/applicants/${order.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create order");
      setSubmitting(false);
    }
  };

  const selectedPackage = packages.find((p) => p.id === form.package_id);
  const basePrice = selectedPackage
    ? (form.delivery_type === "express" ? selectedPackage.price_express : selectedPackage.price_normal)
    : 0;
  const expressSurcharge = selectedPackage && form.delivery_type === "express"
    ? Math.round(selectedPackage.price_express - selectedPackage.price_normal)
    : 0;
  const addOnTotal = addOnPrice(form.add_ons);
  const total = (basePrice || 0) + addOnTotal;

  const toggleAddOn = (id) => {
    setForm((f) => {
      const has = f.add_ons.includes(id);
      return { ...f, add_ons: has ? f.add_ons.filter((x) => x !== id) : [...f.add_ons, id] };
    });
  };

  // Preview cards for sidebar: package items minus WhatsApp support + selected add-on previews.
  // We keep WhatsApp on Premium label/list but it stays separately. The PreviewCard list shows ALL though.
  const previewItems = useMemo(() => {
    const items = (selectedPackage?.items || []).map((it) => ({ ...it, _kind: "pkg" }));
    const adds = form.add_ons.map((id) => {
      const a = ADD_ONS_BY_ID[id];
      return a ? { title: a.preview.title, package_contents: a.preview.package_contents, _kind: "addon", _id: id } : null;
    }).filter(Boolean);
    return [...items, ...adds];
  }, [selectedPackage, form.add_ons]);

  // Documents-only count (excludes WhatsApp Support)
  const docCount = (selectedPackage?.items || []).filter((it) => !isSupportItem(it)).length;
  const supportIncluded = (selectedPackage?.items || []).some(isSupportItem);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <SEO
        title="Start Your Schengen Visa File — Apply Online | SchengenFile"
        description="Fill in your travel details and get your Schengen visa document package prepared within 24 hours by certified travel agents."
      />
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="max-w-2xl">
            <Link to="/" className="text-xs text-[#475569] hover:text-[#0f1b2d]">← Back to home</Link>
            <h1 className="mt-4 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Start your visa file</h1>
            <p className="mt-2 text-[#475569]">Fill in the details below. Documents will be ready within the package delivery time after payment.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <form onSubmit={handleSubmit} data-testid="apply-form" className="lg:col-span-2 bg-white border border-[#e2e8f0] rounded-lg p-6 sm:p-8 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name" required>
                  <input required data-testid="input-full-name" type="text" value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input" />
                </Field>
                <Field label="Email" required>
                  <input required data-testid="input-email" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="WhatsApp number" required hint="We'll contact you here">
                  <PhoneInput
                    value={form.whatsapp}
                    onChange={(v) => setForm({ ...form, whatsapp: v })}
                    testId="input-whatsapp"
                  />
                </Field>
                <Field label="Applicants" required>
                  <input required data-testid="input-applicants" type="number" min={1} max={10}
                    value={form.num_applicants}
                    onChange={(e) => setForm({ ...form, num_applicants: parseInt(e.target.value || "1") })} className="input" />
                </Field>
              </div>

              <Field label="Your nationality" required>
                <CountryCombobox
                  options={WORLD_COUNTRIES}
                  value={form.nationality}
                  onChange={(v) => setForm({ ...form, nationality: (v || "").toUpperCase(), applying_from_country: "", destination_country: "" })}
                  placeholder="Select your nationality…"
                  testId="input-nationality"
                  required
                />
              </Field>

              <Field label="Where are you applying from?" required
                hint="The country where you will attend your visa appointment">
                <CountryCombobox
                  options={WORLD_COUNTRIES}
                  value={form.applying_from_country}
                  onChange={(v) => setForm({ ...form, applying_from_country: (v || "").toUpperCase(), applying_from_city: "", destination_country: "" })}
                  disabled={!form.nationality || isSchengenNational}
                  placeholder="Select country…"
                  testId="input-applying-from"
                  required
                />
              </Field>

              {/* City — appears only after country is selected */}
              {form.applying_from_country && (
                <Field label="Which city are you applying from?" required
                  hint="The city where your visa appointment will take place">
                  <input
                    type="text"
                    required
                    data-testid="input-applying-from-city"
                    value={form.applying_from_city}
                    onChange={(e) => setForm({ ...form, applying_from_city: e.target.value })}
                    placeholder="e.g. Riyadh, Dubai, Istanbul..."
                    className="input"
                  />
                </Field>
              )}

              {/* Info note — only after both city and country are filled */}
              {form.applying_from_country && form.applying_from_city.trim() && applyingFromName && (
                <div
                  data-testid="apply-departure-note"
                  className="-mt-2 flex items-start gap-2.5 rounded-md bg-[#eff6ff] border-l-2 border-[#2563eb] px-3 py-3 text-[12px] leading-relaxed text-[#1e3a8a]"
                >
                  <Info className="w-4 h-4 text-[#2563eb] flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0f1b2d] text-[13px]">
                      <span className="mr-1">📍</span> Your documents will be prepared based on {form.applying_from_city.trim()}, {applyingFromName}
                    </div>
                    <p className="mt-1.5">
                      Your flight reservation will be arranged from the nearest major international airport to {form.applying_from_city.trim()}. If no direct flight is available, we will use the closest available departure point.
                    </p>
                    <p className="mt-1.5">
                      Your hotel booking and travel itinerary will also be aligned with your departure from {form.applying_from_city.trim()}.
                    </p>
                    <p className="mt-1.5">
                      If you have a specific airport or departure preference, please mention it in the Notes field below.
                    </p>
                  </div>
                </div>
              )}

              <Field label="Destination country" required>
                <CountryCombobox
                  options={SCHENGEN_COUNTRIES}
                  value={form.destination_country}
                  onChange={(v) => setForm({ ...form, destination_country: v })}
                  valueField="name"
                  disabled={!form.applying_from_country || blocked}
                  placeholder="Select destination country…"
                  testId="input-destination-country"
                  required
                />
              </Field>

              <EligibilityBanner
                nationalityCode={form.nationality}
                applyingFromCode={form.applying_from_country}
                applyingFromCity={form.applying_from_city}
                destinationName={form.destination_country}
                visaFreeAcknowledged={visaFreeAck}
                onVisaFreeAcknowledge={() => setVisaFreeAck(true)}
                testId="apply-eligibility"
              />

              <Field label="Notes (optional)">
                <textarea data-testid="input-notes" rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input"
                  placeholder="Anything we should know? (specific airline, hotel preferences, embassy requirements...)" />
              </Field>

              {/* Choose your package */}
              <div>
                <label className="text-sm font-semibold text-[#0f1b2d]">Choose your package</label>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {packages.map((p) => {
                    const Icon = PACKAGE_ICON[p.id] || ShieldCheck;
                    const active = form.package_id === p.id;
                    const price = form.delivery_type === "express" ? p.price_express : p.price_normal;
                    return (
                      <button key={p.id} type="button" data-testid={`package-option-${p.id}`}
                        onClick={() => setForm({ ...form, package_id: p.id })}
                        className={`text-left p-4 rounded-md border-2 transition-all ${active ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] hover:border-[#94a3b8]"}`}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${active ? "text-[#2563eb]" : "text-[#475569]"}`} />
                          <span className="font-semibold text-[#0f1b2d]">{p.name}</span>
                          <span className="ml-auto font-display font-bold text-[#0f1b2d]">${Math.round(price)}</span>
                        </div>
                        <div className="mt-1 text-xs text-[#475569]">{labelForItems(p.items || [])}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* OPTIONAL ADD-ONS (only after a package is selected) */}
              {selectedPackage && (
                <div data-testid="addons-section">
                  <label className="text-sm font-semibold text-[#0f1b2d]">Optional add-ons</label>
                  <p className="text-xs text-[#475569] mt-1">Upgrade your file with these extras. Each: <span className="font-semibold text-[#0f1b2d]">+$19</span>.</p>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {ADD_ONS.map((a) => {
                      const checked = form.add_ons.includes(a.id);
                      const Icon = a.icon;
                      return (
                        <button key={a.id} type="button" data-testid={`addon-toggle-${a.id}`}
                          onClick={() => toggleAddOn(a.id)}
                          className={`relative text-left p-4 rounded-md border-2 transition-all ${checked ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#94a3b8]"}`}>
                          <div className={`absolute top-3 right-3 w-5 h-5 rounded-md grid place-items-center transition ${checked ? "bg-[#2563eb] text-white" : "border-2 border-[#cbd5e1] bg-white"}`}>
                            {checked && <Check className="w-3 h-3" strokeWidth={3} />}
                          </div>
                          <div className="flex items-center gap-2 pr-7">
                            <Icon className={`w-4 h-4 ${checked ? "text-[#2563eb]" : "text-[#475569]"}`} />
                            <span className="font-semibold text-[#0f1b2d] text-sm">{a.name}</span>
                          </div>
                          <div className="mt-1 text-[11px] text-[#475569] pr-7">{a.description}</div>
                          <div className="mt-2 inline-flex items-center text-xs font-display font-bold text-[#2563eb]">+${a.price}</div>
                          {/* preview tile */}
                          <div className="mt-3 -mx-1 pointer-events-none" data-testid={`addon-preview-${a.id}`}>
                            <div className="flex justify-center">
                              <PreviewCard item={{ title: a.preview.title, package_contents: a.preview.package_contents }} size="thumb" />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {form.add_ons.length > 0 && (
                    <div className="mt-3 text-xs text-[#0f1b2d]" data-testid="addons-summary">
                      <span className="font-semibold text-[#2563eb]">{form.add_ons.length}</span> add-on{form.add_ons.length === 1 ? "" : "s"} selected · <span className="font-semibold">+${addOnTotal}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery */}
              <div>
                <label className="text-sm font-semibold text-[#0f1b2d]">Delivery</label>
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="delivery-toggle">
                  <button type="button" onClick={() => setForm({ ...form, delivery_type: "normal" })}
                    data-testid="delivery-normal"
                    className={`flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md border-2 transition-all ${form.delivery_type === "normal" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569]"}`}>
                    <Clock className="w-4 h-4" /> 24h Delivery
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, delivery_type: "express" })}
                    data-testid="delivery-express"
                    className={`flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md border-2 transition-all ${form.delivery_type === "express" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569]"}`}>
                    <Zap className="w-4 h-4" /> Express 3h · +$50
                  </button>
                </div>
              </div>

              <button type="submit" disabled={submitting || loading || blocked} data-testid="apply-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-md transition-all">
                {submitting ? "Creating order…" : (<>Continue to applicant details <ArrowRight className="w-4 h-4" /></>)}
              </button>
              {!user && !loading && (
                <p className="text-xs text-[#475569] text-center">
                  You&apos;ll need to <Link to={`/login?redirect=/apply${location.search}`} className="text-[#2563eb] font-semibold">log in</Link> or <Link to="/register" className="text-[#2563eb] font-semibold">create an account</Link> to continue.
                </p>
              )}
            </form>

            {/* ORDER SUMMARY SIDEBAR */}
            <aside data-testid="order-summary" className="bg-white border border-[#e2e8f0] rounded-lg p-6 h-fit sticky top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-[#64748b]">Order summary</div>
              <h3 className="mt-1 font-display font-bold text-xl text-[#0f1b2d]">{selectedPackage?.name || "Package"} package</h3>
              <div className="mt-1 text-xs text-[#475569] flex items-center gap-1">
                {form.delivery_type === "express" ? <Zap className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                {form.delivery_type === "express" ? "Express delivery in 3 hours" : "Delivery in 24 hours"}
              </div>
              <div className="mt-1 text-xs text-[#0f1b2d] font-semibold" data-testid="summary-doc-label">
                {docCount} document{docCount === 1 ? "" : "s"}
                {form.add_ons.length > 0 && (<> + {form.add_ons.length} add-on{form.add_ons.length === 1 ? "" : "s"}</>)}
              </div>

              {/* Live preview cards */}
              {previewItems.length > 0 && (
                <div className="mt-4 -mx-2" data-testid="summary-preview-row">
                  <ScrollRow ariaLabel={`${selectedPackage?.name || ""} preview`}>
                    {previewItems.map((it, i) => (
                      <PreviewCard key={`${it._kind}-${it._id || i}-${it.title}`} item={it} size="thumb" />
                    ))}
                  </ScrollRow>
                </div>
              )}

              {/* Document list */}
              <ul className="mt-5 space-y-2 text-sm text-[#0f1b2d]">
                {(selectedPackage?.items || []).filter((it) => !isSupportItem(it)).map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#16a34a] flex-shrink-0" />
                    <span>{it.title}</span>
                  </li>
                ))}
                {form.add_ons.map((id) => {
                  const a = ADD_ONS_BY_ID[id];
                  return (
                    <li key={id} className="flex items-start gap-2" data-testid={`summary-addon-${id}`}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#2563eb] flex-shrink-0" />
                      <span><span className="font-semibold">{a.name}</span> <span className="text-[#64748b]">(add-on)</span></span>
                    </li>
                  );
                })}
              </ul>

              {/* WhatsApp Support — visually distinct */}
              {supportIncluded && (
                <div className="mt-3 pt-3 border-t border-[#e2e8f0]">
                  <div className="flex items-start gap-2 text-sm">
                    <MessageCircle className="w-4 h-4 mt-0.5 text-[#2563eb] flex-shrink-0" />
                    <span className="text-[#2563eb] font-semibold">24/7 WhatsApp Support</span>
                  </div>
                </div>
              )}

              {/* Itemized price */}
              <div className="mt-6 pt-6 border-t border-[#e2e8f0] space-y-1.5 text-sm" data-testid="price-breakdown">
                <PriceRow label="Package" value={selectedPackage ? `$${Math.round((selectedPackage.price_normal || 0))}` : "—"} />
                {expressSurcharge > 0 && (
                  <PriceRow label="Express surcharge (1h)" value={`+$${expressSurcharge}`} testId="price-express-row" />
                )}
                {form.add_ons.map((id) => {
                  const a = ADD_ONS_BY_ID[id];
                  return <PriceRow key={id} label={a.name} value={`+$${a.price}`} testId={`price-addon-${id}`} />;
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-[#e2e8f0] flex items-baseline justify-between">
                <span className="text-sm text-[#475569]">Total</span>
                <span className="font-display font-black text-3xl text-[#0f1b2d]" data-testid="price-total">
                  {selectedPackage ? `$${Math.round(total)}` : "—"}
                </span>
              </div>
              <div className="mt-3 text-xs text-[#475569]">Secure payment via Stripe. Refund if not delivered on time.</div>

              {/* Mobile-only secondary CTA after Total */}
              <button
                type="button"
                onClick={(e) => {
                  const formEl = document.querySelector('[data-testid="apply-form"]');
                  if (formEl) {
                    if (typeof formEl.requestSubmit === "function") formEl.requestSubmit();
                    else handleSubmit(e);
                  }
                }}
                disabled={submitting || loading || !selectedPackage || blocked}
                data-testid="apply-submit-btn-mobile"
                className="lg:hidden mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-md transition-all"
              >
                {submitting ? "Creating order…" : (<>Continue to applicant details <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </aside>
          </div>
        </div>
      </main>
      <Footer />

      <style>{`.input { width: 100%; border: 1px solid #e2e8f0; background: #fff; color: #0f1b2d; border-radius: 6px; padding: 10px 12px; outline: none; transition: all 0.15s; font-size: 14px; }
        .input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }`}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[#0f1b2d]">
        {label} {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function PriceRow({ label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-center justify-between text-[#0f1b2d]">
      <span className="text-[#475569]">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
